import { toPrivyWalletConnector } from "@privy-io/cross-app-connect/rainbow-kit"

import type { EIP1193EventMap, EIP1193RequestFn, EIP1474Methods } from "viem"
import type { CreateConnectorFn } from "wagmi"

type PrivyConfig = {
  displayName: string
  iconUrl: string
  privyAppId: string
  connectorId: string
}

/**
 * Create a wagmi connector for a Privy Global Wallet.
 *
 * Adapted from wagmi injected connector as a reference implementation:
 * https://github.com/wevm/wagmi/blob/main/packages/core/src/connectors/injected.ts#L94
 */
function createPrivyGlobalWalletConnector(
  config: PrivyConfig,
): CreateConnectorFn<{
  on: <event extends keyof EIP1193EventMap>(
    event: event,
    listener: EIP1193EventMap[event],
  ) => void
  removeListener: <event extends keyof EIP1193EventMap>(
    event: event,
    listener: EIP1193EventMap[event],
  ) => void
  request: EIP1193RequestFn<EIP1474Methods>
}> {
  return params => {
    const connector = toPrivyWalletConnector({
      iconUrl: config.iconUrl,
      id: config.privyAppId,
      name: config.displayName,
    })(params)

    const getProvider = async (
      parameters?: { chainId?: number | undefined } | undefined,
    ) => {
      // default to mainnet
      const chainId = parameters?.chainId ?? params.chains?.[0]?.id ?? 1
      return await connector.getProvider({
        chainId,
      })
    }

    return {
      ...connector,
      getProvider,
      type: "injected",
      id: config.connectorId,
    }
  }
}

export { createPrivyGlobalWalletConnector }
